import * as React from 'react';
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList } from '../Combobox';

interface FolderComboboxProps {
	folders: string[];
	onSelect: (folder: string) => void;
	onClose?: () => void;
}

/**
 * Picks an existing folder or names a new one. Deliberately separate from the template keyword combobox,
 * which parses a `folder:` prefix out of a shared keyword field; library folders are a field of their own
 * and carry no prefix, so reusing that component would mean importing behavior this surface must not have.
 */
export function FolderCombobox({ folders, onSelect, onClose }: FolderComboboxProps) {
	const [inputValue, setInputValue] = React.useState('');
	const trimmedInput = inputValue.trim();

	const matches = folders.filter(folder => folder.toLowerCase().includes(trimmedInput.toLowerCase()));
	const isExistingFolder = folders.some(folder => folder.toLowerCase() === trimmedInput.toLowerCase());
	const canCreate = !!trimmedInput && !isExistingFolder;
	const items = canCreate ? [...matches, trimmedInput] : matches;

	// The shared Combobox wrapper resolves base-ui's generic to unknown, so its callbacks arrive untyped
	// for every consumer. Narrowing here beats an unchecked cast; widening the wrapper would touch every
	// combobox in the app.
	return (
		<Combobox
			filter={null}
			inputValue={inputValue}
			isItemEqualToValue={(item: unknown, value: unknown) => item === value}
			items={items}
			itemToStringLabel={(item: unknown) => String(item)}
			onInputValueChange={setInputValue}
			onOpenChange={(open: boolean) => {
				if (!open) {
					onClose?.();
				}
			}}
			onValueChange={(value: unknown) => {
				if (typeof value === 'string' && value) {
					setInputValue('');
					onSelect(value);
				}
			}}
			value={null}
		>
			<ComboboxInput autoFocus placeholder='Search or name a folder' showTrigger={false} />
			<ComboboxContent>
				<ComboboxList>
					{matches.map(folder => (
						<ComboboxItem key={folder} value={folder}>
							{folder}
						</ComboboxItem>
					))}
					{canCreate ? <ComboboxItem value={trimmedInput}>{`Create folder "${trimmedInput}"`}</ComboboxItem> : null}
				</ComboboxList>
				{!items.length ? <ComboboxEmpty>Start typing to name a folder</ComboboxEmpty> : null}
			</ComboboxContent>
		</Combobox>
	);
}
